import React from 'react';
import {
  TIERS, FROM_PRICE, ENTRY_PRICE_NOTE, CUSTOM_QUOTE, AI_ACT, DSA, DSA_PRICE_SECOND, VAT_EXCLUDED, TAX_LINE, usd,
} from '@/lib/pricing';
import { RESOURCE_CTAS, STARTUP_FORM_URL } from '@/content/resources/ctas';
import { SERVICES } from '@/content/resources/services';

// Article rail cards (rail.html): <RailCta> keyed to the article's service,
// then <StartupCard>, always. Preview colours mapped to site tokens: navy
// #003399, gold #FFCC00, ink on gold #111827, gold-deep #E6B800 (decorative
// pixels only). Prices and terms come from src/lib/pricing.js.

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const NAVY = '#003399';
const GOLD_DEEP = '#E6B800';

// taxLine: show TAX_LINE under the card, for articles with no <PricingSummary>
// (that card's page footnote carries it otherwise; once per page).
const Tax = ({ show }) => (show ? <p className="rs-rail-tax">{TAX_LINE}</p> : null);

export const RailCta = ({ service = 'gdpr', taxLine = false }) => {
  if (service === 'gdpr') {
    const t = TIERS[0];
    const cta = RESOURCE_CTAS['gdpr-signup'];
    return (
      <section className="rs-rail-cta" aria-label="GDPR representative pricing">
        <p className="plan">GDPR representative</p>
        <p className="price"><span className="from">From</span><span className="amount">${FROM_PRICE}</span><span className="per">per month</span></p>
        <p className="terms">EU and UK coverage. {cap(ENTRY_PRICE_NOTE)}, for companies under {t.revenueLimit} in revenue. {VAT_EXCLUDED}.</p>
        <a className="rs-rail-btn gold" href={cta.href}>{cta.button}</a>
        <Tax show={taxLine} />
      </section>
    );
  }
  const s = SERVICES[service];
  // AI Act: published from-price, links to the service page.
  if (service === 'ai-act') {
    return (
      <section className="rs-rail-cta" aria-label={`${s.name} pricing`}>
        <p className="plan">{s.name}</p>
        <p className="price"><span className="from">From</span><span className="amount">{usd(AI_ACT.from)}</span><span className="per">per {AI_ACT.period}</span></p>
        <p className="terms">{VAT_EXCLUDED}. For one high-risk AI system.</p>
        <a className="rs-rail-btn gold" href={s.href}>How the service works</a>
        <Tax show={taxLine} />
      </section>
    );
  }
  // DSA: monthly rate billed annually, plus a setup fee; quotes go to /contact/.
  if (service === 'dsa') {
    return (
      <section className="rs-rail-cta" aria-label={`${s.name} pricing`}>
        <p className="plan">{s.name}</p>
        <p className="price"><span className="amount">{usd(DSA.monthly)}</span><span className="per">per month, billed annually</span></p>
        <p className="terms">{DSA_PRICE_SECOND}. {VAT_EXCLUDED}.</p>
        <a className="rs-rail-btn gold" href="/contact/">Request a quote</a>
        <p className="proof">We reply within one business day.</p>
        <Tax show={taxLine} />
      </section>
    );
  }
  return (
    <section className="rs-rail-cta" aria-label={`${s.name} pricing`}>
      <p className="plan">{s.name}</p>
      <p className="price"><span className="quote">{CUSTOM_QUOTE.basis[service]}</span></p>
      <a className="rs-rail-btn gold" href="/contact/">Request a quote</a>
      <p className="proof">We reply within one business day.</p>
      <Tax show={taxLine} />
    </section>
  );
};

// 16x16 pixel art, drawn with rects and crispEdges as in the preview.
const Px = ({ cells, fill }) => <g fill={fill}>{cells.map(([x, y, w, h]) => <rect key={`${x}-${y}-${w}`} x={x} y={y} width={w} height={h} />)}</g>;

const ROCKET = [[7, 1, 2, 1], [6, 2, 4, 1], [6, 3, 4, 1], [5, 4, 6, 1], [5, 5, 6, 1], [5, 6, 6, 1], [5, 7, 6, 1], [5, 8, 6, 1], [5, 9, 6, 1],
  [3, 8, 2, 1], [11, 8, 2, 1], [2, 9, 3, 1], [11, 9, 3, 1], [2, 10, 12, 1], [2, 11, 3, 1], [11, 11, 3, 1], [6, 11, 4, 1]];
const HEART = [[2, 3, 3, 1], [9, 3, 3, 1], [1, 4, 5, 1], [8, 4, 5, 1], [1, 5, 12, 1], [1, 6, 12, 1], [2, 7, 10, 1], [3, 8, 8, 1],
  [4, 9, 6, 1], [5, 10, 4, 1], [6, 11, 2, 1]];
const CHECK = [[2, 8, 2, 2], [4, 10, 2, 2], [6, 8, 2, 2], [8, 6, 2, 2], [10, 4, 2, 2], [12, 2, 2, 2]];

const Rocket = () => (
  <svg className="rocket" viewBox="0 0 16 16" shapeRendering="crispEdges" aria-hidden="true" focusable="false">
    <Px cells={ROCKET} fill={NAVY} />
    <Px cells={[[7, 4, 2, 2]]} fill="#FFFFFF" />
    <Px cells={[[6, 12, 4, 1], [7, 13, 2, 1], [7, 14, 2, 1]]} fill={GOLD_DEEP} />
    <Px cells={[[7, 12, 2, 1]]} fill={NAVY} />
  </svg>
);
const Heart = () => (
  <svg viewBox="0 0 16 16" shapeRendering="crispEdges" role="img" aria-label="love" focusable="false"><Px cells={HEART} fill={NAVY} /></svg>
);
const Check = () => (
  <svg viewBox="0 0 16 16" shapeRendering="crispEdges" aria-hidden="true" focusable="false"><Px cells={CHECK} fill={NAVY} /></svg>
);

export const StartupCard = () => (
  <section className="rs-startup" aria-label="Startup Program">
    <Rocket />
    <h3><span>We</span><Heart /><span>startups</span></h3>
    <p>Reduced monthly rate, the full platform, and a real person to ask.</p>
    <ul>
      <li><Check /><span>Companies and solo builders</span></li>
      {/* When the eligibility form ships (STARTUP_FORM_URL in ctas.js), this
          bullet becomes "Instant eligibility check". */}
      <li><Check /><span>Full platform included</span></li>
    </ul>
    <a className="rs-rail-btn navy" href={STARTUP_FORM_URL}>Apply to the program</a>
  </section>
);
